import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Volume2, VolumeX, AlertTriangle, Mic, MicOff } from "lucide-react";

export const DecibelMeter = () => {
  const [isListening, setIsListening] = useState(false);
  const [decibels, setDecibels] = useState(0);
  const [peak, setPeak] = useState(0);
  const [alertThreshold] = useState(90);
  const [hasAlert, setHasAlert] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      analyserRef.current.fftSize = 256;
      
      setIsListening(true);
      setPermissionDenied(false);
      toast.success("Microphone activated");
      
      measureDecibels();
    } catch (error) {
      console.error("Microphone access denied:", error);
      setPermissionDenied(true);
      toast.error("Microphone access denied");
    }
  };

  const stopListening = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setIsListening(false);
    setDecibels(0);
    toast.success("Microphone deactivated");
  }, []);

  const measureDecibels = () => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
    const measure = () => {
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate RMS (Root Mean Square) for more accurate dB reading
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length);
      
      // Convert to decibels (rough approximation)
      // Normalize to 0-120 dB range for display
      const db = Math.round((rms / 255) * 120);
      
      setDecibels(db);
      setPeak(prev => Math.max(prev, db));
      
      // Check for alert
      if (db >= alertThreshold) {
        setHasAlert(true);
      } else {
        setHasAlert(false);
      }
      
      animationRef.current = requestAnimationFrame(measure);
    };
    
    measure();
  };

  useEffect(() => {
    return () => {
      if (isListening) {
        stopListening();
      }
    };
  }, [isListening, stopListening]);

  // Play alert sound when threshold exceeded
  useEffect(() => {
    if (hasAlert && isListening) {
      // Visual alert only (browser notification could be added)
      document.title = "⚠️ LOUD - Event Pro Toolbox";
    } else {
      document.title = "Event Pro Toolbox";
    }
  }, [hasAlert, isListening]);

  const getDecibelColor = (db) => {
    if (db < 60) return "bg-green-500";
    if (db < 75) return "bg-yellow-500";
    if (db < 90) return "bg-orange-500";
    return "bg-red-500";
  };

  const getDecibelTextColor = (db) => {
    if (db < 60) return "text-green-400";
    if (db < 75) return "text-yellow-400";
    if (db < 90) return "text-orange-400";
    return "text-red-400";
  };

  const resetPeak = () => {
    setPeak(0);
    toast.success("Peak reset");
  };

  // Generate bars for visualization
  const bars = Array.from({ length: 30 }, (_, i) => {
    const threshold = (i / 30) * 120;
    const isActive = decibels >= threshold;
    let colorClass = "bg-green-500";
    if (threshold >= 60) colorClass = "bg-yellow-500";
    if (threshold >= 75) colorClass = "bg-orange-500";
    if (threshold >= 90) colorClass = "bg-red-500";
    return { isActive, colorClass };
  });

  return (
    <Layout 
      title="Decibel Meter" 
      subtitle="Monitor venue noise levels with visual alerts"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Main Display */}
        <Card className={`tactical-card ${hasAlert ? 'border-red-500 animate-pulse' : 'border-yellow-500/30'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg uppercase text-yellow-400">
                <Volume2 className="h-5 w-5" />
                Sound Level
              </CardTitle>
              {hasAlert && (
                <div className="flex items-center gap-2 text-red-400 animate-pulse">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="text-sm font-bold uppercase">Alert!</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Decibel Display */}
            <div className="text-center py-4">
              <p className={`text-7xl md:text-8xl font-mono font-bold ${getDecibelTextColor(decibels)}`} data-testid="decibel-display">
                {decibels}
              </p>
              <p className="text-xl text-muted-foreground mt-2">dB</p>
            </div>

            {/* Bar Visualization */}
            <div className="flex items-end justify-center gap-1 h-32" data-testid="decibel-bars">
              {bars.map((bar, i) => (
                <div
                  key={i}
                  className={`w-2 md:w-3 rounded-t transition-all duration-75 ${
                    bar.isActive ? bar.colorClass : 'bg-secondary'
                  }`}
                  style={{ 
                    height: bar.isActive ? `${((i + 1) / 30) * 100}%` : '10%',
                    opacity: bar.isActive ? 1 : 0.3
                  }}
                />
              ))}
            </div>

            {/* Level Indicators */}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0 dB</span>
              <span className="text-green-400">Safe &lt;60</span>
              <span className="text-yellow-400">Moderate 60-75</span>
              <span className="text-orange-400">Loud 75-90</span>
              <span className="text-red-400">Danger &gt;90</span>
              <span>120 dB</span>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              {!isListening ? (
                <Button 
                  onClick={startListening}
                  className="tactical-btn bg-green-600 hover:bg-green-700 w-40"
                  disabled={permissionDenied}
                  data-testid="start-listening-button"
                >
                  <Mic className="h-5 w-5 mr-2" />
                  Start
                </Button>
              ) : (
                <Button 
                  onClick={stopListening}
                  className="tactical-btn bg-red-600 hover:bg-red-700 w-40"
                  data-testid="stop-listening-button"
                >
                  <MicOff className="h-5 w-5 mr-2" />
                  Stop
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Card */}
        <Card className="tactical-card">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="label-tactical">Current</p>
                <p className={`text-2xl font-mono font-bold ${getDecibelTextColor(decibels)}`}>
                  {decibels} dB
                </p>
              </div>
              <div>
                <p className="label-tactical">Peak</p>
                <p className={`text-2xl font-mono font-bold ${getDecibelTextColor(peak)}`}>
                  {peak} dB
                </p>
              </div>
              <div>
                <p className="label-tactical">Alert At</p>
                <p className="text-2xl font-mono font-bold text-red-400">
                  {alertThreshold} dB
                </p>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetPeak}
                data-testid="reset-peak-button"
              >
                Reset Peak
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Permission Warning */}
        {permissionDenied && (
          <Card className="tactical-card border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-red-400">
                <VolumeX className="h-5 w-5" />
                <div>
                  <p className="font-semibold">Microphone Access Denied</p>
                  <p className="text-sm text-muted-foreground">
                    Please enable microphone access in your browser settings to use this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="tactical-card">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2">Sound Level Guide</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-muted-foreground">&lt;60 dB - Quiet conversation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-muted-foreground">60-75 dB - Normal speech</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-muted-foreground">75-90 dB - Busy venue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-muted-foreground">&gt;90 dB - Hearing risk</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DecibelMeter;
